import { useCallback } from 'react';

import { GA4_EVENT_VIEW_ITEM } from '@/data/constants/gtm';
import { getGTMConfig } from '@/data/events/handlers/gtm';
import { isB2BStore, useSettings } from '@/data/Settings';
import { logEventWithValues } from '@/data/utils/logEventsUtils';
import { useMobileApp } from '@/utils/useMobileApp';
import { B2B, B2C } from 'presentation/events/constants/eventConstants';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { triggerGA4EcommerceEvent } from 'presentation/events/services/ga4EcommerceService';
import { buildGA4EcommerceEventPayload } from 'presentation/events/utils/ga4Utils';
import { useGetCategories } from 'shared/custom/hooks/Categories';

import { SendGA4EventProps } from './types';
import { getProductCategoryArray } from './utils';

export const useTrackViewItemGA4 = () => {
	const { settings } = useSettings();
	const { navigation } = useGetCategories();
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const { isMobileApp } = useMobileApp();
	const isB2B = isB2BStore(settings);

	return useCallback(
		({ products, bundleId }: SendGA4EventProps) => {
			const { ga4 } = getGTMConfig(settings);

			if (!ga4 || !physicalStoreId) return;

			const payload = buildGA4EcommerceEventPayload({
				event: GA4_EVENT_VIEW_ITEM,
				settings,
				physicalStoreId,
				bundleId,
				items: products.map((product) => ({
					product,
					categories: getProductCategoryArray(product, navigation),
					quantity: product.quantity ?? 1,
				})),
			});

			payload.ecommerce.items = payload.ecommerce.items.map((item, index) => {
				const giftType = products[index]?.giftType;
				return giftType ? { ...item, gift_type: giftType } : item;
			});

			if (!isMobileApp) {
				triggerGA4EcommerceEvent(payload, { cleanup: true });
				return;
			}

			logEventWithValues(payload.event, {
				...payload.ecommerce,
				app_thd: !isB2B ? B2C : B2B,
			});
		},
		[navigation, isB2B, isMobileApp, physicalStoreId, settings]
	);
};
